import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useMerchantRole } from "@/hooks/useMerchantRole";
import { useSubscription } from "@/hooks/useSubscription";
import { useTeamMemberProfile } from "@/hooks/useTeamMemberProfile";
import { useNavigationBlocker, setBypassBlocker } from "@/hooks/useNavigationBlocker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  Users, 
  Clock, 
  CheckCircle, 
  Loader2,
  Search,
  ExternalLink,
  Wallet,
  Package,
  Store,
  LogOut,
  Bell,
  Receipt,
  Gift,
  RotateCcw,
  Banknote,
  Settings,
  Menu,
  Briefcase,
  Activity,
  Plus
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import { toast } from "sonner";
import MerchantSettings from "@/components/MerchantSettings";
import MerchantPreferences from "@/components/MerchantPreferences";
import { TrialBanner } from "@/components/TrialBanner";
import { SubscriptionPayment } from "@/components/SubscriptionPayment";
import { PaymentHistory } from "@/components/PaymentHistory";
import { FinancialStatement } from "@/components/FinancialStatement";
import { MerchantAnalytics } from "@/components/MerchantAnalytics";
import ProductShakatahManager from "@/components/ProductShakatahManager";
import { CustomerInsights } from "@/components/CustomerInsights";
import { FAQChatBot } from "@/components/FAQChatBot";
import { DashboardOverview } from "@/components/DashboardOverview";
import { TeamMemberSetup } from "@/components/TeamMemberSetup";
import { DashboardTour } from "@/components/DashboardTour";
import { ContactSupport } from "@/components/ContactSupport";
import { ReviewRequestDialog } from "@/components/ReviewRequestDialog";
import { MerchantSidebar, PlatformTheme } from "@/components/MerchantSidebar";
import { ProfessionalSidebar, type ProfessionalTerminology, type PaymentsSubView, type ServicesSubView, type ReportsSubView } from "@/components/ProfessionalSidebar";
import { ProfessionalOverview } from "@/components/ProfessionalOverview";
import { ClientList } from "@/components/professional/ClientList";
import { ServicesList } from "@/components/professional/ServicesList";
import { CreateCollectionDialog } from "@/components/professional/CreateCollectionDialog";
import { NotificationSettings } from "@/components/NotificationSettings";
import { ExitConfirmationDialog } from "@/components/ExitConfirmationDialog";
import { WidgetUpdateBanner, CURRENT_WIDGET_VERSION } from "@/components/WidgetUpdateBanner";
import { DashboardSwitcher } from "@/components/DashboardSwitcher";
import { CustomerFooter } from "@/components/CustomerFooter";
import RefundManagement from "@/components/RefundManagement";
import { CreditCard, UserCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

interface Participant {
  id: string;
  name: string;
  email: string;
  mobile: string;
  has_paid: boolean;
  midpoint_reminder_sent: boolean;
  final_reminder_sent: boolean;
}

interface SplitPaymentRequest {
  id: string;
  product_name: string;
  product_image_url: string | null;
  product_description: string | null;
  total_price: number;
  split_amount: number;
  deadline: string;
  deadline_type: string;
  customer_name: string;
  customer_email: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  tax_rate: number;
  participants: Participant[];
}

interface GiftVoucher {
  id: string;
  voucher_number: string;
  amount: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  status: string;
  issued_at: string;
  redeemed_at: string | null;
  participant_name?: string;
  participant_email?: string;
  product_name?: string;
}

interface RedemptionInvoice {
  id: string;
  invoice_number: string;
  product_name: string;
  product_amount: number;
  total_vouchers_redeemed: number;
  total_voucher_amount: number;
  redeemed_at: string;
}

interface CancellationFee {
  id: string;
  request_id: string;
  fee_amount: number;
  merchant_share: number;
  shakatah_share: number;
  status: string;
  created_at: string;
  invoiced_at: string | null;
  paid_at: string | null;
  product_name?: string;
  customer_name?: string;
}

// Professional collections interface (from client_subscriptions + client_payments)
interface ProfessionalCollection {
  id: string;
  service_name: string;
  description: string | null;
  frequency: string;
  amount: number;
  currency: string;
  status: string;
  start_date: string;
  next_due_date: string | null;
  client_id: string;
  client_name: string;
  client_email: string;
  created_at: string;
  payments: {
    id: string;
    amount: number;
    status: string;
    due_date: string;
    paid_at: string | null;
    tranche_label: string | null;
  }[];
}

interface ShakatahInvoice {
  id: string;
  invoice_number: string;
  period_start: string;
  period_end: string;
  total_fees: number;
  total_amount: number;
  status: string;
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
}

interface MerchantInfo {
  id: string;
  store_name: string;
  shopify_store_domain: string;
  notification_email: string | null;
  plan: string;
  business_address?: string | null;
  gst_number?: string | null;
  business_email?: string | null;
  business_phone?: string | null;
  contact_person_name?: string | null;
  currency?: string;
  widget_version?: number | null;
  widget_synced_at?: string | null;
  platform_type?: string;
  onboarding_completed_at?: string | null;
  professional_terminology?: Record<string, string> | null;
}

const MerchantDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const { role: merchantRole, isSuperAdmin, isManager, isViewOnly, canManageRefunds, canManageProducts, canAccessSettings, merchantId: roleMerchantId, loading: roleLoading } = useMerchantRole();
  const { subscription, loading: subscriptionLoading } = useSubscription();
  const { needsSetup, needsTour, refetch: refetchProfile } = useTeamMemberProfile(roleMerchantId);
  const [requests, setRequests] = useState<SplitPaymentRequest[]>([]);
  const [professionalCollections, setProfessionalCollections] = useState<ProfessionalCollection[]>([]);
  const [vouchers, setVouchers] = useState<GiftVoucher[]>([]);
  const [redemptionInvoices, setRedemptionInvoices] = useState<RedemptionInvoice[]>([]);
  const [cancellationFees, setCancellationFees] = useState<CancellationFee[]>([]);
  const [shakatahInvoices, setShakatahInvoices] = useState<ShakatahInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "completed" | "expired">("all");
  const [voucherFilter, setVoucherFilter] = useState<"all" | "issued" | "redeemed" | "pending_refund" | "refunded">("all");
  const [merchantInfo, setMerchantInfo] = useState<MerchantInfo | null>(null);
  const [markingRefund, setMarkingRefund] = useState<string | null>(null);
  const [bulkRefundProcessing, setBulkRefundProcessing] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showCreateCollectionDialog, setShowCreateCollectionDialog] = useState(false);

  // Navigation blocker to confirm before leaving dashboard
  const { 
    showDialog: showExitDialog, 
    confirmNavigation, 
    cancelNavigation 
  } = useNavigationBlocker({ when: true });

  // Get navigation state from URL params for proper browser history support
  const activeTab = searchParams.get("tab") || "overview";
  const ordersSubView = (searchParams.get("ordersView") as "requests" | "vouchers" | "refunds") || "requests";
  const insightsSubView = (searchParams.get("insightsView") as "analytics" | "customers") || "analytics";
  const settingsSubView = (searchParams.get("settingsView") as "billing" | "preferences" | "notifications" | "info") || "billing";
  
  // Professional-specific navigation state
  const paymentsSubView = (searchParams.get("paymentsView") as PaymentsSubView) || "collections";
  const servicesSubView = (searchParams.get("servicesView") as ServicesSubView) || "services";
  const reportsSubView = (searchParams.get("reportsView") as ReportsSubView) || "analytics";

  // Setters for professional sub-views
  const setPaymentsSubView = useCallback((view: PaymentsSubView) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("paymentsView", view);
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);

  const setServicesSubView = useCallback((view: ServicesSubView) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("servicesView", view);
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);

  const setReportsSubView = useCallback((view: ReportsSubView) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("reportsView", view);
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);

  // Navigation functions that update URL (enables browser back button support)
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", tab);
      // Reset sub-views when changing main tab
      newParams.delete("ordersView");
      newParams.delete("insightsView");
      newParams.delete("settingsView");
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);

  const setOrdersSubView = useCallback((view: "requests" | "vouchers" | "refunds") => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("ordersView", view);
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);

  const setInsightsSubView = useCallback((view: "analytics" | "customers") => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("insightsView", view);
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);

  const setSettingsSubView = useCallback((view: "billing" | "preferences" | "notifications" | "info") => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("settingsView", view);
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);

  // Helper to set both tab and subview when navigating to billing
  // MUST be defined before any early returns to avoid React hooks order violation
  const handleNavigateToBilling = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", "settings");
      newParams.set("settingsView", "billing");
      newParams.delete("ordersView");
      newParams.delete("insightsView");
      return newParams;
    }, { replace: false });
  }, [setSearchParams]);
  
  // Check if team member needs setup (non-super_admin only)
  useEffect(() => {
    if (!roleLoading && !isSuperAdmin && needsSetup) {
      setShowSetup(true);
    } else if (!roleLoading && !isSuperAdmin && needsTour) {
      setShowTour(true);
    }
  }, [roleLoading, isSuperAdmin, needsSetup, needsTour]);
  

  const fetchMerchantInfo = async () => {
    if (!user) return null;

    try {
      // Get the user's merchant association
      const { data: merchantUser, error: merchantUserError } = await supabase
        .from('merchant_users')
        .select('merchant_id, role')
        .eq('user_id', user.id)
        .maybeSingle();

      if (merchantUserError || !merchantUser) {
        console.error("No merchant association found:", merchantUserError);
        toast.error("No store linked to your account. Please contact support.");
        return null;
      }

      // Get merchant details
      const { data: merchant, error: merchantError } = await supabase
        .from('merchants')
        .select('*')
        .eq('id', merchantUser.merchant_id)
        .single();

      if (merchantError || !merchant) {
        console.error("Failed to fetch merchant:", merchantError);
        return null;
      }

      setMerchantInfo(merchant as unknown as MerchantInfo);
      return merchantUser.merchant_id;
    } catch (error) {
      console.error("Error fetching merchant info:", error);
      return null;
    }
  };

  const fetchRequests = async (merchantId: string) => {
    try {
      // Fetch split payment requests for this merchant only
      const { data: requestsData, error: requestsError } = await supabase
        .from('split_payment_requests')
        .select('*')
        .eq('merchant_id', merchantId)
        .order('created_at', { ascending: false });

      if (requestsError) throw requestsError;

      if (!requestsData || requestsData.length === 0) {
        setRequests([]);
      } else {
        const requestIds = requestsData.map(r => r.id);
        const { data: participantsData, error: participantsError } = await supabase
          .from('split_payment_participants')
          .select('*')
          .in('request_id', requestIds);

        if (participantsError) throw participantsError;

        const combinedRequests = requestsData.map(request => ({
          ...request,
          participants: (participantsData || []).filter(p => p.request_id === request.id)
        }));

        setRequests(combinedRequests);
      }

      // Fetch gift vouchers for this merchant's requests
      const { data: vouchersData, error: vouchersError } = await supabase
        .from('gift_vouchers')
        .select('*, split_payment_participants(name, email), split_payment_requests!inner(product_name, merchant_id)')
        .eq('split_payment_requests.merchant_id', merchantId)
        .order('issued_at', { ascending: false });

      if (!vouchersError && vouchersData) {
        const formattedVouchers = vouchersData.map(v => ({
          ...v,
          participant_name: v.split_payment_participants?.name,
          participant_email: v.split_payment_participants?.email,
          product_name: v.split_payment_requests?.product_name,
        }));
        setVouchers(formattedVouchers);
      }

      // Fetch redemption invoices for this merchant's requests
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('redemption_invoices')
        .select('*, split_payment_requests!inner(merchant_id)')
        .eq('split_payment_requests.merchant_id', merchantId)
        .order('redeemed_at', { ascending: false });

      if (!invoicesError && invoicesData) {
        setRedemptionInvoices(invoicesData);
      }

      // Fetch cancellation fees for this merchant
      const { data: feesData, error: feesError } = await supabase
        .from('cancellation_fees')
        .select('*, split_payment_requests(product_name, customer_name)')
        .eq('merchant_id', merchantId)
        .order('created_at', { ascending: false });

      if (!feesError && feesData) {
        const formattedFees = feesData.map(f => ({
          ...f,
          product_name: f.split_payment_requests?.product_name,
          customer_name: f.split_payment_requests?.customer_name,
        }));
        setCancellationFees(formattedFees);
      }

      // Fetch Shakatah invoices for this merchant
      const { data: shakatahInvoicesData, error: shakatahInvoicesError } = await supabase
        .from('shakatah_invoices')
        .select('*')
        .eq('merchant_id', merchantId)
        .order('created_at', { ascending: false });

      if (!shakatahInvoicesError && shakatahInvoicesData) {
        setShakatahInvoices(shakatahInvoicesData);
      }

      // Fetch professional collections (client_subscriptions with payments)
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from('client_subscriptions')
        .select(`
          *,
          professional_clients(name, email),
          client_payments(id, amount, status, due_date, paid_at, tranche_label)
        `)
        .eq('merchant_id', merchantId)
        .order('created_at', { ascending: false });

      if (!subscriptionsError && subscriptionsData) {
        const formattedCollections: ProfessionalCollection[] = subscriptionsData.map(sub => ({
          id: sub.id,
          service_name: sub.service_name,
          description: sub.description,
          frequency: sub.frequency,
          amount: sub.amount,
          currency: sub.currency,
          status: sub.status,
          start_date: sub.start_date,
          next_due_date: sub.next_due_date,
          client_id: sub.client_id,
          client_name: (sub.professional_clients as any)?.name || 'Unknown',
          client_email: (sub.professional_clients as any)?.email || '',
          created_at: sub.created_at,
          payments: (sub.client_payments as any[]) || [],
        }));
        setProfessionalCollections(formattedCollections);
      }

    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const [noMerchantAssociation, setNoMerchantAssociation] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      const merchantId = await fetchMerchantInfo();
      if (merchantId) {
        await fetchRequests(merchantId);
      } else {
        // No merchant found - user signed up but merchant record wasn't created
        setNoMerchantAssociation(true);
        setLoading(false);
      }
    };
    loadData();
  }, [user]);

  // Redirect to onboarding if not completed or no merchant association - hard block
  // But only redirect if the onboarding page can handle the case (has account repair logic)
  useEffect(() => {
    if (loading) return;
    
    // Handle case where user has no merchant association at all
    // Check if there's pending Shopify data - if so, redirect to merchant onboarding
    // Otherwise, default to professional onboarding for account repair
    if (noMerchantAssociation) {
      toast.info("Please complete your account setup to continue");
      // Bypass navigation blocker for redirect to onboarding
      setBypassBlocker(true);
      
      // Check for pending Shopify installation context
      const pendingShopifyShop = sessionStorage.getItem("pending_shopify_shop");
      const isShopifyFlow = pendingShopifyShop || window.location.search.includes("platform=shopify");
      
      // Route to appropriate onboarding based on context
      const onboardingPath = isShopifyFlow ? '/merchant/onboarding' : '/professional/onboarding';
      navigate(onboardingPath, { replace: true });
      setTimeout(() => setBypassBlocker(false), 100);
      return;
    }
    
    if (!merchantInfo) return;
    
    // If onboarding is not completed, redirect to appropriate onboarding
    if (!merchantInfo.onboarding_completed_at) {
      const domain = merchantInfo.shopify_store_domain || '';
      const platformType = merchantInfo.platform_type;
      
      // Determine which onboarding to redirect to
      const isProfessional = platformType === 'standalone' && domain.includes('.professional.');
      const onboardingPath = isProfessional ? '/professional/onboarding' : '/merchant/onboarding';
      
      toast.info("Please complete your setup to continue");
      // Bypass navigation blocker for redirect to onboarding
      setBypassBlocker(true);
      navigate(onboardingPath, { replace: true });
      setTimeout(() => setBypassBlocker(false), 100);
    }
  }, [loading, merchantInfo, noMerchantAssociation, navigate]);

  // Only run once when role loading completes, not on every settingsSubView change
  useEffect(() => {
    if (!roleLoading && !canAccessSettings && settingsSubView === 'billing') {
      // Only redirect from billing (the default) - don't interfere with manual navigation
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set("settingsView", "notifications");
        return newParams;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleLoading, canAccessSettings]);

  const handleSignOut = async () => {
    // Bypass the navigation blocker for sign-out
    const { setBypassBlocker } = await import("@/hooks/useNavigationBlocker");
    setBypassBlocker(true);
    
    await signOut();
    // Use replace to remove dashboard from history, preventing forward navigation back
    navigate("/merchant-login", { replace: true });
    toast.success("Signed out successfully");
    
    // Reset bypass flag (cleanup)
    setBypassBlocker(false);
  };

  const markRefundComplete = async (voucherId: string) => {
    try {
      setMarkingRefund(voucherId);
      const { error } = await supabase
        .from('gift_vouchers')
        .update({ status: 'refunded' })
        .eq('id', voucherId);

      if (error) throw error;

      // Send notification email to participant
      try {
        await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-refund-complete`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ voucher_id: voucherId }),
          }
        );
      } catch (emailError) {
        console.error("Failed to send refund notification email:", emailError);
        // Don't fail the whole operation if email fails
      }

      setVouchers(prev => prev.map(v => 
        v.id === voucherId ? { ...v, status: 'refunded' } : v
      ));

      toast.success('Refund marked as complete - participant notified');
    } catch (error) {
      console.error("Error marking refund complete:", error);
      toast.error("Failed to update refund status");
    } finally {
      setMarkingRefund(null);
    }
  };

  const markAllRefundsComplete = async () => {
    const pendingRefundVouchers = vouchers.filter(v => v.status === 'pending_refund');
    if (pendingRefundVouchers.length === 0) {
      toast.info('No pending refunds to process');
      return;
    }

    setBulkRefundProcessing(true);
    let successCount = 0;
    let failCount = 0;

    for (const voucher of pendingRefundVouchers) {
      try {
        const { error } = await supabase
          .from('gift_vouchers')
          .update({ status: 'refunded' })
          .eq('id', voucher.id);

        if (error) throw error;

        // Send notification email
        try {
          await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-refund-complete`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ voucher_id: voucher.id }),
            }
          );
        } catch (emailError) {
          console.error("Failed to send refund notification email:", emailError);
        }

        successCount++;
      } catch (error) {
        console.error("Error marking refund complete:", error);
        failCount++;
      }
    }

    // Update local state
    setVouchers(prev => prev.map(v => 
      v.status === 'pending_refund' ? { ...v, status: 'refunded' } : v
    ));

    setBulkRefundProcessing(false);

    if (failCount === 0) {
      toast.success(`All ${successCount} refunds marked as complete - participants notified`);
    } else {
      toast.warning(`${successCount} refunds completed, ${failCount} failed`);
    }
  };

  const getRequestStatus = (status: string, deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const isExpired = now > deadlineDate;

    if (status === 'completed') return 'completed';
    if (status === 'expired' || isExpired) return 'expired';
    return 'pending';
  };

  const getStatusBadge = (status: string, deadline: string) => {
    const actualStatus = getRequestStatus(status, deadline);

    if (actualStatus === 'completed') {
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Completed</Badge>;
    }
    if (actualStatus === 'expired') {
      return <Badge variant="destructive">Expired</Badge>;
    }
    return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">Pending</Badge>;
  };

  const getTimeRemaining = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate.getTime() - now.getTime();

    if (diff <= 0) return "Expired";

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h left`;
    }
    return `${hours}h ${minutes}m left`;
  };

  const getPaidCount = (participants: Participant[]) => {
    const paid = participants.filter(p => p.has_paid).length;
    return { paid, total: participants.length };
  };


  // Filter requests
  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      request.participants.some(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const actualStatus = getRequestStatus(request.status, request.deadline);
    const matchesStatus = statusFilter === "all" || actualStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });


  if (loading || roleLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show team member setup for non-super_admin
  if (showSetup && merchantInfo && user && merchantRole) {
    return (
      <TeamMemberSetup
        merchantId={merchantInfo.id}
        userId={user.id}
        role={merchantRole}
        storeName={merchantInfo.store_name}
        onComplete={() => {
          setShowSetup(false);
          refetchProfile();
          setShowTour(true);
        }}
      />
    );
  }



  // Determine platform theme based on platform_type and domain
  const getPlatformTheme = (): PlatformTheme => {
    const platformType = merchantInfo?.platform_type;
    const domain = merchantInfo?.shopify_store_domain || '';
    
    if (platformType === 'standalone' && domain.includes('.professional.')) {
      return 'professional';
    }
    if (platformType === 'shopify') {
      return 'shopify';
    }
    if (platformType === 'woocommerce') {
      return 'woocommerce';
    }
    return 'standalone'; // Default for direct/standalone stores
  };
  
  const platformTheme = getPlatformTheme();

  // Helper to get theme classes for header
  const getHeaderThemeClasses = () => {
    switch (platformTheme) {
      case 'shopify':
        return { bg: 'bg-shopify/10', text: 'text-shopify', border: 'border-shopify/50' };
      case 'woocommerce':
        return { bg: 'bg-woocommerce/10', text: 'text-woocommerce', border: 'border-woocommerce/50' };
      case 'professional':
        return { bg: 'bg-professional/10', text: 'text-professional', border: 'border-professional/50' };
      default:
        return { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/50' };
    }
  };
  
  const headerTheme = getHeaderThemeClasses();

  // Get platform label for badge
  const getPlatformLabel = () => {
    switch (platformTheme) {
      case 'shopify': return 'Shopify';
      case 'woocommerce': return 'WooCommerce';
      case 'professional': return 'Professional';
      default: return 'Direct Store';
    }
  };

  // Sidebar component (shared between mobile and desktop)
  const isProfessional = platformTheme === 'professional';
  
  const sidebarContent = isProfessional ? (
    <ProfessionalSidebar
      activeTab={activeTab}
      paymentsSubView={paymentsSubView}
      servicesSubView={servicesSubView}
      reportsSubView={reportsSubView}
      settingsSubView={settingsSubView}
      onTabChange={(tab, subView) => {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set("tab", tab);
          newParams.delete("paymentsView");
          newParams.delete("servicesView");
          newParams.delete("reportsView");
          newParams.delete("settingsView");
          if (subView) {
            if (tab === "payments") newParams.set("paymentsView", subView);
            else if (tab === "services") newParams.set("servicesView", subView);
            else if (tab === "reports") newParams.set("reportsView", subView);
            else if (tab === "settings") newParams.set("settingsView", subView);
          }
          return newParams;
        }, { replace: false });
        setMobileSidebarOpen(false);
      }}
      onPaymentsSubViewChange={setPaymentsSubView}
      onServicesSubViewChange={setServicesSubView}
      onReportsSubViewChange={setReportsSubView}
      onSettingsSubViewChange={setSettingsSubView}
      pendingPaymentsCount={requests.filter(r => r.status === 'pending').length}
      pendingFeesCount={cancellationFees.filter(f => f.status === 'pending').length}
      canAccessSettings={canAccessSettings}
      terminology={merchantInfo?.professional_terminology as ProfessionalTerminology || undefined}
    />
  ) : (
    <MerchantSidebar
      activeTab={activeTab}
      ordersSubView={ordersSubView}
      insightsSubView={insightsSubView}
      settingsSubView={settingsSubView}
      onTabChange={(tab, subView) => {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set("tab", tab);
          newParams.delete("ordersView");
          newParams.delete("insightsView");
          newParams.delete("settingsView");
          if (subView) {
            if (tab === "orders") newParams.set("ordersView", subView);
            else if (tab === "insights") newParams.set("insightsView", subView);
            else if (tab === "settings") newParams.set("settingsView", subView);
          }
          return newParams;
        }, { replace: false });
        setMobileSidebarOpen(false);
      }}
      onOrdersSubViewChange={setOrdersSubView}
      onInsightsSubViewChange={setInsightsSubView}
      onSettingsSubViewChange={setSettingsSubView}
      pendingOrdersCount={requests.filter(r => r.status === 'pending').length}
      pendingRefundsCount={vouchers.filter(v => v.status === 'pending_refund' || v.status === 'refund_failed').length}
      pendingFeesCount={cancellationFees.filter(f => f.status === 'pending').length}
      canManageProducts={canManageProducts}
      canAccessSettings={canAccessSettings}
      widgetNeedsUpdate={isSuperAdmin && (merchantInfo?.widget_version ?? 1) < CURRENT_WIDGET_VERSION}
      platformTheme={platformTheme}
    />
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Dashboard Tour for team members */}
      {showTour && user && (isManager || isViewOnly) && (
        <DashboardTour
          userId={user.id}
          role={isManager ? 'manager' : 'view_only'}
          run={showTour}
          onComplete={() => setShowTour(false)}
        />
      )}
      
      {/* Review Request Dialog - shows after 100 splits or 1 month of paid subscription */}
      {merchantInfo && subscription && isSuperAdmin && (
        <ReviewRequestDialog
          merchantId={merchantInfo.id}
          merchantName={merchantInfo.store_name}
          totalSplits={requests.length}
          subscriptionStartDate={subscription.plan !== 'trial' ? (merchantInfo as any).billing_cycle_start : null}
          plan={subscription.plan}
        />
      )}
      
      {/* Widget Update Banner - shows when widget version is outdated (only for Shopify/WooCommerce) */}
      {merchantInfo && isSuperAdmin && !isProfessional && (merchantInfo.platform_type === 'shopify' || merchantInfo.platform_type === 'woocommerce') && (
        <WidgetUpdateBanner
          merchantId={merchantInfo.id}
          merchantWidgetVersion={merchantInfo.widget_version ?? null}
          onResyncComplete={async () => {
            // Refresh merchant info to get updated version
            const merchantId = await fetchMerchantInfo();
            if (merchantId) await fetchRequests(merchantId);
          }}
        />
      )}
      
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-20">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Mobile menu trigger */}
              <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-64">
                  {sidebarContent}
                </SheetContent>
              </Sheet>
              
              <div className={cn("p-2 rounded-lg hidden sm:block", headerTheme.bg)}>
                <Store className={cn("h-5 w-5", headerTheme.text)} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-semibold text-sm sm:text-base">{merchantInfo?.contact_person_name || merchantInfo?.store_name || 'Dashboard'}</h1>
                  <Badge 
                    variant="outline" 
                    className={cn(
                      "text-xs hidden sm:inline-flex",
                      headerTheme.border,
                      headerTheme.text,
                      headerTheme.bg
                    )}
                  >
                    {getPlatformLabel()}
                  </Badge>
                  {merchantRole && (
                    <Badge variant="outline" className="capitalize text-xs hidden sm:inline-flex">
                      {merchantRole.replace('_', ' ')}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground hidden sm:block">
                  {user?.email}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Dashboard Switcher - only visible for Professionals (not Shopify/WooCommerce merchants) */}
              {isProfessional && user?.email && (
                <DashboardSwitcher
                  currentDashboard="professional"
                  userEmail={user.email}
                />
              )}
              <ContactSupport 
                merchantName={merchantInfo?.store_name}
                merchantEmail={merchantInfo?.notification_email || undefined}
              />
              {merchantInfo?.shopify_store_domain && platformTheme === 'shopify' && (
                <Button 
                  size="sm" 
                  className="bg-[#96bf48] hover:bg-[#7a9e3a] text-white hidden sm:flex"
                  onClick={() => window.open(`https://${merchantInfo.shopify_store_domain}/admin`, '_blank')}
                >
                  <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.337 3.415c-.193-.016-.4.013-.544.064-.144.05-.514.193-.667.257-.153.065-.41.193-.514.224-.103.032-.205.046-.256.064-.052.016-.116.032-.167.032h-.064c-.116-.064-.244-.145-.373-.193-.129-.048-.27-.08-.41-.096-.142-.016-.283-.016-.373.016-.09.032-.18.08-.27.144-.09.065-.18.145-.257.225-.077.08-.142.16-.206.24-.064.081-.116.161-.167.225-.052.064-.09.112-.116.144l-.09.129c-.038.048-.077.112-.128.192-.052.08-.116.176-.18.304-.064.129-.129.273-.193.449-.065.177-.116.385-.168.625-.05.241-.077.514-.077.803 0 .289.026.593.077.882.052.288.129.56.206.802.077.24.154.449.244.624.09.177.18.32.257.433.077.112.142.192.193.256.052.065.09.097.116.113l.052.048c.026.016.052.048.09.08.039.032.09.08.155.129.064.048.128.096.206.144.077.048.167.096.257.145.09.048.193.08.296.112.103.032.206.048.296.048.09 0 .167-.016.232-.032.064-.016.116-.032.154-.064.039-.032.065-.064.078-.096.012-.032.012-.064.012-.08v-.032c0-.016-.012-.032-.012-.048 0-.016-.013-.032-.026-.064-.012-.032-.038-.064-.064-.112-.026-.048-.064-.08-.103-.129-.039-.048-.077-.08-.116-.112-.039-.032-.077-.064-.116-.08-.038-.016-.077-.032-.103-.048-.025-.016-.051-.032-.064-.032-.012-.016-.025-.016-.038-.016h-.026c-.013 0-.026 0-.052.016-.025.016-.064.032-.115.064-.052.032-.116.064-.18.112-.065.048-.142.096-.219.16-.078.065-.155.129-.232.193-.078.065-.155.129-.22.177-.064.048-.115.08-.154.097-.038.016-.064.016-.077.016h-.026z"/>
                  </svg>
                  Shopify
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleSignOut}
                className="gap-1 sm:gap-2"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign Out</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout with Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block h-[calc(100vh-57px)] sticky top-[57px]">
          {sidebarContent}
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto pb-12">
          <div className="p-4 sm:p-6 max-w-5xl">
            {/* Trial/Subscription Banner */}
            {subscription && (
              <TrialBanner
                plan={subscription.plan}
                status={subscription.status}
                daysRemaining={subscription.daysRemaining}
                isTrialExpired={subscription.isTrialExpired}
                splitsThisMonth={subscription.splitsThisMonth}
                splitLimit={subscription.splitLimit}
                onUpgradeClick={handleNavigateToBilling}
                isProfessional={isProfessional}
                onExtendTrialClick={() => {
                  const subject = encodeURIComponent(`${isProfessional ? 'Free Period' : 'Trial'} Extension Request - ${merchantInfo?.store_name || 'My Business'}`);
                  const body = encodeURIComponent(`Hi Shakatah Team,\n\nI would like to request an extension for my ${isProfessional ? 'free period' : 'trial'}.\n\n${isProfessional ? 'Business' : 'Store'}: ${merchantInfo?.store_name || 'N/A'}\n${isProfessional ? 'Collections processed' : 'Splits used'}: ${subscription.splitsThisMonth}${subscription.splitLimit ? `/${subscription.splitLimit}` : ''}\n\nThank you!`);
                  window.open(`mailto:hello@shakatah.com?subject=${subject}&body=${body}`, '_blank');
                }}
              />
            )}

            {/* Content Sections */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                {isProfessional ? (
                  <ProfessionalOverview
                    requests={requests}
                    merchantInfo={merchantInfo}
                    subscription={subscription}
                    terminology={merchantInfo?.professional_terminology as ProfessionalTerminology || undefined}
                    onNavigateToTab={(tab, subView) => {
                      setSearchParams(prev => {
                        const newParams = new URLSearchParams(prev);
                        newParams.set("tab", tab);
                        newParams.delete("paymentsView");
                        newParams.delete("servicesView");
                        newParams.delete("reportsView");
                        newParams.delete("settingsView");
                        if (tab === "settings" && subView) {
                          newParams.set("settingsView", subView);
                        } else if (subView) {
                          if (tab === "payments") newParams.set("paymentsView", subView);
                          else if (tab === "services") newParams.set("servicesView", subView);
                          else if (tab === "reports") newParams.set("reportsView", subView);
                        }
                        return newParams;
                      }, { replace: false });
                    }}
                    onCreateCollection={() => setShowCreateCollectionDialog(true)}
                  />
                ) : (
                  <DashboardOverview
                    requests={requests}
                    merchantInfo={merchantInfo}
                    subscription={subscription}
                    onNavigateToTab={(tab, subView) => {
                      setSearchParams(prev => {
                        const newParams = new URLSearchParams(prev);
                        newParams.set("tab", tab);
                        newParams.delete("ordersView");
                        newParams.delete("insightsView");
                        newParams.delete("settingsView");
                        if (tab === "settings" && subView) {
                          newParams.set("settingsView", subView);
                        }
                        return newParams;
                      }, { replace: false });
                    }}
                  />
                )}
              </div>
            )}

            {/* Professional: Payments Tab */}
            {isProfessional && activeTab === "payments" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">{merchantInfo?.professional_terminology?.orders || "Payments"}</h2>
                    <p className="text-sm text-muted-foreground">
                      Manage your payment collections
                    </p>
                  </div>
                  <Button 
                    className="bg-cyan-600 hover:bg-cyan-700"
                    onClick={() => setShowCreateCollectionDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Collection
                  </Button>
                </div>
                {/* Render payment collections list - reuse existing orders UI */}
                {paymentsSubView === "collections" && (
                  <div className="space-y-4">
                    {requests.length === 0 ? (
                      <Card className="text-center py-12">
                        <CardContent>
                          <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="font-medium mb-2">No collections yet</h3>
                          <p className="text-sm text-muted-foreground mb-4">
                            Create your first payment collection to get started
                          </p>
                          <Button 
                            className="bg-cyan-600 hover:bg-cyan-700"
                            onClick={() => setShowCreateCollectionDialog(true)}
                          >
                            Create Collection
                          </Button>
                        </CardContent>
                      </Card>
                    ) : (
                      filteredRequests.map((request) => {
                        const { paid, total } = getPaidCount(request.participants);
                        return (
                          <Card key={request.id} className="overflow-hidden">
                            <div className="p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h3 className="font-semibold">{request.product_name}</h3>
                                  <p className="text-sm text-muted-foreground">
                                    {paid}/{total} paid • {new Date(request.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                {getStatusBadge(request.status, request.deadline)}
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{formatCurrency(request.total_price, (merchantInfo?.currency as any) || 'INR')}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(`/split/${request.id}`, '_blank')}
                                >
                                  <ExternalLink className="h-4 w-4 mr-1" />
                                  View
                                </Button>
                              </div>
                            </div>
                          </Card>
                        );
                      })
                    )}
                  </div>
                )}
                {paymentsSubView === "history" && (
                  <PaymentHistory merchantId={merchantInfo?.id || ''} />
                )}
              </div>
            )}

            {/* Professional: Services & Clients Tab */}
            {isProfessional && activeTab === "services" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">
                      {merchantInfo?.professional_terminology?.products || "Services"} & {merchantInfo?.professional_terminology?.customers || "Clients"}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Manage your service offerings and client relationships
                    </p>
                  </div>
                </div>
                {servicesSubView === "services" && merchantInfo && (
                  <ServicesList
                    merchantId={merchantInfo.id}
                    currency={merchantInfo.currency || "INR"}
                    terminology={merchantInfo.professional_terminology as ProfessionalTerminology || undefined}
                  />
                )}
                {servicesSubView === "clients" && merchantInfo && (
                  <ClientList
                    merchantId={merchantInfo.id}
                    terminology={merchantInfo.professional_terminology as ProfessionalTerminology || undefined}
                  />
                )}
              </div>
            )}

            {/* Professional: Reports Tab */}
            {isProfessional && activeTab === "reports" && (
              <div className="space-y-6">
                {reportsSubView === "analytics" && (
                  <MerchantAnalytics 
                    requests={requests}
                    splitsThisMonth={subscription?.splitsThisMonth || 0}
                    splitLimit={subscription?.splitLimit || null}
                    currency={(merchantInfo?.currency as any) || 'INR'}
                    isProfessional={true}
                    terminology={merchantInfo?.professional_terminology as Record<string, string> || undefined}
                    onNavigateToOrders={(filter) => {
                      setSearchParams(prev => {
                        const newParams = new URLSearchParams(prev);
                        newParams.set("tab", "payments");
                        newParams.set("paymentsView", "history");
                        if (filter) newParams.set("statusFilter", filter);
                        return newParams;
                      }, { replace: false });
                    }}
                  />
                )}
                {reportsSubView === "activity" && (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-medium mb-2">Activity log coming soon</h3>
                      <p className="text-sm text-muted-foreground">
                        Track all your recent actions and payments
                      </p>
                    </CardContent>
                  </Card>
                )}
                {reportsSubView === "customers" && (
                  <CustomerInsights requests={requests} />
                )}
              </div>
            )}

            {activeTab === "insights" && (
              <div className="space-y-6">

            {insightsSubView === "analytics" && (
              <MerchantAnalytics 
                requests={requests}
                splitsThisMonth={subscription?.splitsThisMonth || 0}
                splitLimit={subscription?.splitLimit || null}
                currency={(merchantInfo?.currency as any) || 'USD'}
                onNavigateToOrders={(filter) => {
                  setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.set("tab", "orders");
                    newParams.set("ordersView", "history");
                    if (filter) newParams.set("statusFilter", filter);
                    return newParams;
                  }, { replace: false });
                }}
              />
            )}

            {insightsSubView === "customers" && (
              <CustomerInsights requests={requests} />
            )}
              </div>
            )}

            {activeTab === "orders" && (
              <div className="space-y-6">
                {/* Requests Sub-View */}
                {ordersSubView === "requests" && (
                  <div className="space-y-6">
                    {/* Search and Filters */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search by product, organizer, or participant..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      <div className="flex gap-2">
                        {(["all", "pending", "completed", "expired"] as const).map((status) => (
                          <Button
                            key={status}
                            variant={statusFilter === status ? "default" : "outline"}
                            size="sm"
                            onClick={() => setStatusFilter(status)}
                            className="capitalize"
                          >
                            {status}
                          </Button>
                        ))}
                      </div>
                    </div>

                {/* Orders List */}
                {filteredRequests.length === 0 && requests.length > 0 ? (
                  <div className="text-center py-12">
                    <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h2 className="text-lg font-semibold mb-2">No matching orders</h2>
                    <p className="text-muted-foreground">
                      Try adjusting your search or filter criteria
                    </p>
                  </div>
                ) : requests.length === 0 ? (
                  <div className="text-center py-20">
                    <Users className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                    <h2 className="text-xl font-semibold mb-2">No split payment orders yet</h2>
                    <p className="text-muted-foreground mb-6">
                      Split payment orders will appear here
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredRequests.map((request) => {
                      const { paid, total } = getPaidCount(request.participants);
                      const allPaid = paid === total;

                      return (
                        <Card key={request.id} className="overflow-hidden">
                          <div className="flex">
                            {request.product_image_url && (
                              <div className="w-32 h-32 flex-shrink-0 bg-muted">
                                <img 
                                  src={request.product_image_url} 
                                  alt={request.product_name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            <div className="flex-1 p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h3 className="font-semibold">{request.product_name}</h3>
                                  <p className="text-sm text-muted-foreground">
                                    by {request.customer_name} • {new Date(request.created_at).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {getStatusBadge(request.status, request.deadline)}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => window.open(`/split/${request.id}`, '_blank')}
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                              <div className="flex items-center gap-6 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Total: </span>
                                  <span className="font-medium">{formatCurrency(request.total_price)}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Per Person: </span>
                                  <span className="font-medium text-primary">{formatCurrency(request.split_amount)}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Progress: </span>
                                  <span className={`font-medium ${allPaid ? 'text-green-600' : ''}`}>
                                    {paid}/{total} paid
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Clock className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-muted-foreground">{getTimeRemaining(request.deadline)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Vouchers Sub-View */}
            {ordersSubView === "vouchers" && (
              <div className="space-y-6">
            {/* Voucher Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Gift className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Vouchers</p>
                      <p className="text-2xl font-bold">{vouchers.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-500/10 rounded-lg">
                      <Receipt className="h-4 w-4 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Issued</p>
                      <p className="text-2xl font-bold text-yellow-600">{vouchers.filter(v => v.status === 'issued').length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/10 rounded-lg">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Redeemed</p>
                      <p className="text-2xl font-bold text-green-600">{vouchers.filter(v => v.status === 'redeemed').length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500/10 rounded-lg">
                      <Wallet className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Value</p>
                      <p className="text-lg font-bold text-green-600">{formatCurrency(vouchers.reduce((sum, v) => sum + Number(v.total_amount), 0))}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filter and Bulk Actions */}
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex gap-2 flex-wrap">
                {(["all", "issued", "redeemed", "pending_refund", "refunded"] as const).map((status) => (
                  <Button
                    key={status}
                    variant={voucherFilter === status ? "default" : "outline"}
                    size="sm"
                    onClick={() => setVoucherFilter(status)}
                    className="capitalize"
                  >
                    {status === 'pending_refund' ? 'Pending Refund' : status}
                  </Button>
                ))}
              </div>
              
              {canManageRefunds && vouchers.filter(v => v.status === 'pending_refund').length > 0 && (
                <Button
                  onClick={markAllRefundsComplete}
                  disabled={bulkRefundProcessing}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {bulkRefundProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark All Refunds Complete ({vouchers.filter(v => v.status === 'pending_refund').length})
                    </>
                  )}
                </Button>
              )}
            </div>

            {/* Vouchers List */}
            <div className="space-y-3">
              {vouchers.filter(v => voucherFilter === "all" || v.status === voucherFilter).map((voucher) => (
                <Card key={voucher.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          voucher.status === 'redeemed' ? 'bg-green-500/10' : 
                          voucher.status === 'pending_refund' ? 'bg-orange-500/10' :
                          voucher.status === 'refunded' ? 'bg-blue-500/10' :
                          'bg-yellow-500/10'
                        }`}>
                          {voucher.status === 'pending_refund' || voucher.status === 'refunded' ? (
                            <RotateCcw className={`h-5 w-5 ${
                              voucher.status === 'refunded' ? 'text-blue-600' : 'text-orange-600'
                            }`} />
                          ) : (
                            <Gift className={`h-5 w-5 ${voucher.status === 'redeemed' ? 'text-green-600' : 'text-yellow-600'}`} />
                          )}
                        </div>
                        <div>
                          <p className="font-mono font-semibold text-primary">{voucher.voucher_number}</p>
                          <p className="text-sm text-muted-foreground">{voucher.participant_name} • {voucher.product_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold">{formatCurrency(voucher.total_amount)}</p>
                          <Badge className={
                            voucher.status === 'redeemed' ? 'bg-green-500/10 text-green-600' : 
                            voucher.status === 'pending_refund' ? 'bg-orange-500/10 text-orange-600' :
                            voucher.status === 'refunded' ? 'bg-blue-500/10 text-blue-600' :
                            'bg-yellow-500/10 text-yellow-600'
                          }>
                            {voucher.status === 'pending_refund' ? 'Pending Refund' : voucher.status}
                          </Badge>
                        </div>
                        {canManageRefunds && voucher.status === 'pending_refund' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markRefundComplete(voucher.id)}
                            disabled={markingRefund === voucher.id}
                            className="border-green-500/30 text-green-600 hover:bg-green-500/10"
                          >
                            {markingRefund === voucher.id ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <CheckCircle className="h-4 w-4 mr-1" />
                            )}
                            Mark Refund Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {vouchers.length === 0 && (
                <div className="text-center py-12">
                  <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No gift vouchers yet</p>
                </div>
              )}
            </div>

            {/* Redemption Invoices */}
            {redemptionInvoices.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Redemption Invoices</CardTitle>
                  <CardDescription>Completed split payment redemptions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {redemptionInvoices.map((invoice) => (
                      <div key={invoice.id} className="flex items-center justify-between p-3 rounded-lg border">
                        <div>
                          <p className="font-mono font-semibold text-sm">{invoice.invoice_number}</p>
                          <p className="text-sm text-muted-foreground">{invoice.product_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-600">{formatCurrency(invoice.total_voucher_amount)}</p>
                          <p className="text-xs text-muted-foreground">{invoice.total_vouchers_redeemed} vouchers</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              )}
              </div>
            )}

            {/* Refunds Sub-View */}
            {ordersSubView === "refunds" && merchantInfo && (
              <RefundManagement 
                merchantId={merchantInfo.id}
                currency={merchantInfo.currency || 'INR'}
              />
            )}
            </div>
          )}

            {/* Products Tab - super_admin and manager only */}
            {activeTab === "products" && canManageProducts && merchantInfo && (
              <div className="space-y-6">
                <ProductShakatahManager 
                  merchantId={merchantInfo.id}
                  hasToken={true}
                />
              </div>
            )}

            {activeTab === "settings" && (
              <div className="space-y-6">
                {/* Billing Sub-View - super_admin only */}
                {settingsSubView === "billing" && canAccessSettings && (
                  <div className="space-y-6">
                    {/* Subscription & Billing Section */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <CreditCard className="h-5 w-5" />
                          Subscription & Billing
                        </CardTitle>
                        <CardDescription>
                          {subscription?.status === 'trialing' 
                            ? `You're on a free trial. ${subscription.daysRemaining ? `${subscription.daysRemaining} days remaining.` : ''}`
                            : subscription?.status === 'active'
                            ? `Current plan: ${subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)}`
                            : 'Choose a plan to continue using Shakatah'}
                        </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {merchantInfo && subscription && (
                      <SubscriptionPayment
                        merchantId={merchantInfo.id}
                        currentPlan={subscription.plan}
                        currency={(merchantInfo?.currency as any) || 'INR'}
                        isProfessional={isProfessional}
                        onSuccess={async () => {
                          if (merchantInfo) {
                            await fetchRequests(merchantInfo.id);
                            window.location.reload();
                          }
                        }}
                      />
                    )}
                  </CardContent>
                </Card>

                {/* Payment History */}
                {merchantInfo && (
                  <PaymentHistory merchantId={merchantInfo.id} />
                )}

                {/* Fee Stats - Only for Merchants (not professionals) */}
                {!isProfessional && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card>
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-lg">
                              <Banknote className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Total Fees</p>
                              <p className="text-2xl font-bold">{cancellationFees.length}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-500/10 rounded-lg">
                              <Wallet className="h-4 w-4 text-green-600" />
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Your Revenue</p>
                              <p className="text-xl font-bold text-green-600">
                                {formatCurrency(cancellationFees.reduce((sum, f) => sum + Number(f.merchant_share), 0))}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-yellow-500/10 rounded-lg">
                              <Clock className="h-4 w-4 text-yellow-600" />
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Pending</p>
                              <p className="text-2xl font-bold text-yellow-600">
                                {cancellationFees.filter(f => f.status === 'pending').length}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-500/10 rounded-lg">
                              <Receipt className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Shakatah Share</p>
                              <p className="text-xl font-bold text-blue-600">
                                {formatCurrency(cancellationFees.reduce((sum, f) => sum + Number(f.shakatah_share), 0))}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Info Card */}
                    <Card className="border-primary/20 bg-primary/5">
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-start gap-3">
                          <Banknote className="h-5 w-5 text-primary mt-0.5" />
                          <div>
                            <h4 className="font-medium mb-1">How Cancellation Fees Work</h4>
                            <p className="text-sm text-muted-foreground">
                              When a split payment request expires without full payment, a cancellation fee is charged 
                              for holding inventory. This fee is split 50/50 between you and Shakatah. Pending fees will 
                              be included in your next invoice.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pending Fees Info */}
                    {cancellationFees.filter(f => f.status === 'pending').length > 0 && (
                      <Card className="border-yellow-500/20 bg-yellow-500/5">
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-start gap-3">
                            <Clock className="h-5 w-5 text-yellow-600 mt-0.5" />
                            <div>
                              <h4 className="font-medium mb-1">Pending Fees</h4>
                              <p className="text-sm text-muted-foreground">
                                You have {cancellationFees.filter(f => f.status === 'pending').length} pending fee(s) 
                                totaling {formatCurrency(cancellationFees.filter(f => f.status === 'pending').reduce((sum, f) => sum + Number(f.shakatah_share), 0))} in Shakatah's share. 
                                These will be included in your next monthly invoice from Shakatah.
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}

                {/* Shakatah Invoices */}
                {shakatahInvoices.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Receipt className="h-5 w-5" />
                        Shakatah Invoices
                      </CardTitle>
                      <CardDescription>Your billing history with Shakatah</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {shakatahInvoices.map((invoice) => (
                          <div key={invoice.id} className="flex items-center justify-between p-4 rounded-lg border">
                            <div className="flex items-center gap-4">
                              <div className={`p-2 rounded-lg ${
                                invoice.status === 'paid' ? 'bg-green-500/10' : 'bg-blue-500/10'
                              }`}>
                                <Receipt className={`h-5 w-5 ${
                                  invoice.status === 'paid' ? 'text-green-600' : 'text-blue-600'
                                }`} />
                              </div>
                              <div>
                                <p className="font-mono font-semibold">{invoice.invoice_number}</p>
                                <p className="text-sm text-muted-foreground">
                                  {new Date(invoice.period_start).toLocaleDateString()} - {new Date(invoice.period_end).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-semibold">{formatCurrency(invoice.total_amount)}</p>
                                <p className="text-xs text-muted-foreground">{invoice.total_fees} fee(s)</p>
                              </div>
                              <Badge className={
                                invoice.status === 'paid' ? 'bg-green-500/10 text-green-600' : 
                                invoice.status === 'sent' ? 'bg-blue-500/10 text-blue-600' :
                                'bg-yellow-500/10 text-yellow-600'
                              }>
                                {invoice.status === 'paid' ? 'Paid' : 
                                 invoice.status === 'sent' ? 'Sent' : 'Draft'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Fees List - Merchants only */}
                {!isProfessional && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Banknote className="h-5 w-5" />
                      Fee Details
                    </CardTitle>
                    <CardDescription>Individual cancellation fee records</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {cancellationFees.length === 0 ? (
                        <div className="text-center py-12">
                          <Banknote className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground">No cancellation fees yet</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Fees are generated when split payment orders expire
                          </p>
                        </div>
                      ) : (
                        cancellationFees.map((fee) => (
                          <div key={fee.id} className="flex items-center justify-between p-3 rounded-lg border">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${
                                fee.status === 'paid' ? 'bg-green-500/10' : 
                                fee.status === 'invoiced' ? 'bg-blue-500/10' :
                                'bg-yellow-500/10'
                              }`}>
                                <Banknote className={`h-4 w-4 ${
                                  fee.status === 'paid' ? 'text-green-600' : 
                                  fee.status === 'invoiced' ? 'text-blue-600' :
                                  'text-yellow-600'
                                }`} />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{fee.product_name || 'Split Payment'}</p>
                                <p className="text-xs text-muted-foreground">
                                  {fee.customer_name} • {new Date(fee.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="font-semibold text-green-600">+{formatCurrency(fee.merchant_share)}</p>
                                <p className="text-xs text-muted-foreground">
                                  of {formatCurrency(fee.fee_amount)} fee
                                </p>
                              </div>
                              <Badge className={
                                fee.status === 'paid' ? 'bg-green-500/10 text-green-600' : 
                                fee.status === 'invoiced' ? 'bg-blue-500/10 text-blue-600' :
                                'bg-yellow-500/10 text-yellow-600'
                              }>
                                {fee.status === 'paid' ? 'Paid' : 
                                 fee.status === 'invoiced' ? 'Invoiced' : 'Pending'}
                              </Badge>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
                )}

                {/* Financial Statement Download */}
                {merchantInfo && (
                  <FinancialStatement 
                    merchantId={merchantInfo.id}
                    merchantInfo={{
                      store_name: merchantInfo.store_name,
                      shopify_store_domain: merchantInfo.shopify_store_domain,
                      business_address: merchantInfo.business_address,
                      gst_number: merchantInfo.gst_number,
                      business_email: merchantInfo.business_email,
                      business_phone: merchantInfo.business_phone,
                    }}
                    currency={(merchantInfo?.currency as any) || 'USD'}
                    isProfessional={isProfessional}
                  />
                )}
              </div>
            )}

            {/* Preferences Sub-View - super_admin only */}
            {settingsSubView === "preferences" && canAccessSettings && merchantInfo && (
              <MerchantPreferences
                merchantId={merchantInfo.id}
                storeDomain={merchantInfo.shopify_store_domain}
                canEdit={canAccessSettings}
                currentPlan={subscription?.plan}
                onNavigateToBilling={() => setSettingsSubView("billing")}
                merchantEmail={merchantInfo.notification_email}
                isSuperAdmin={isSuperAdmin}
                platformType={merchantInfo.platform_type}
                terminology={merchantInfo.professional_terminology as Record<string, string> | undefined}
              />
            )}

            {/* Notifications Sub-View */}
            {settingsSubView === "notifications" && merchantInfo && (
              <NotificationSettings
                merchantId={merchantInfo.id}
                canEdit={canAccessSettings || !isViewOnly}
                merchantEmail={merchantInfo.notification_email || user?.email}
                isProfessional={isProfessional}
                terminology={merchantInfo.professional_terminology as Record<string, string> | undefined}
              />
            )}

            {/* Store Info Sub-View - super_admin only */}
            {settingsSubView === "info" && canAccessSettings && merchantInfo && (
              <MerchantSettings
                merchantId={merchantInfo.id}
                storeDomain={merchantInfo.shopify_store_domain}
                storeName={merchantInfo.store_name}
                canEdit={canAccessSettings}
                isSuperAdmin={isSuperAdmin}
                currentPlan={merchantInfo.plan}
                onNavigateToBilling={() => setSettingsSubView("billing")}
                isProfessional={isProfessional}
              />
            )}
              </div>
            )}
          </div>
        </main>
      </div>
      
      <FAQChatBot context="merchant" />
      
      {/* Exit confirmation dialog */}
      <ExitConfirmationDialog
        open={showExitDialog}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
      />
      
      {/* Create Collection Dialog for Professionals */}
      {merchantInfo && isProfessional && (
        <CreateCollectionDialog
          open={showCreateCollectionDialog}
          onOpenChange={setShowCreateCollectionDialog}
          merchantId={merchantInfo.id}
          currency={(merchantInfo.currency || 'INR') as any}
          terminology={merchantInfo.professional_terminology as ProfessionalTerminology || undefined}
          onSuccess={() => fetchRequests(merchantInfo.id)}
        />
      )}
      
      {/* Footer for Dashboard */}
      {isProfessional ? (
        <CustomerFooter tagline="Simplifying professional payments" variant="professional" />
      ) : (
        <CustomerFooter tagline="Powering split payments for merchants" variant="merchant" />
      )}
    </div>
  );
};

export default MerchantDashboard;
